/**
 * Authentication API service
 * All auth-related API calls should go through this service
 * This will communicate with the main process via IPC
 */

import { FunctionsFetchError } from '@supabase/supabase-js';
import {
  LAST_USER_ID_KEY,
  USER_PROFILE_CACHE_KEY,
} from '../lib/auth/sessionStorage';
import { BILLING_TRIAL_PERIOD_DAYS } from '../../../utils/constants';
import type { ApiResponse } from '../../shared/types';
import {
  BillingCycle,
  LoginResponse,
  SignupPayload,
  SignupResponse,
  User,
  UserRole,
  VerifyEmailResponse,
} from '../types/index'; // types/index will need to re-export shared types
import { isElectron } from '../util';
import { supabase } from './supabase';

import { db } from '../lib/powersync/database';
import { ensureOutletSync } from '../lib/powersync/ensureOutletSync';

// Since we updated types/index.ts in renderer/types to export from shared/types
// we can use those here.

// ── Offline user-profile cache ────────────────────────────────────────────────
// After a successful online login, the user profile is saved to localStorage so
// that the app can function (read-only) when the device has no internet access.
//
// Keyed by user id (not a single global slot) because a shared POS terminal
// has many different waiters/cashiers signing in and out throughout a shift —
// caching only the most-recently-logged-in user would break offline login
// for everyone else who has ever used the same machine. Each entry also
// carries that user's own passcode hash, since offline passcode login (see
// loginUserWithCode) must be able to identify WHICH cached user a passcode
// belongs to without an email to key off of.

interface CachedUserEntry {
  profile: User;
  passcodeHash?: string;
}

function getCacheMap(): Record<string, CachedUserEntry> {
  try {
    const raw = localStorage.getItem(USER_PROFILE_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, CachedUserEntry>) : {};
  } catch {
    return {};
  }
}

function saveCacheMap(map: Record<string, CachedUserEntry>): void {
  try {
    localStorage.setItem(USER_PROFILE_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* ignore — localStorage unavailable */
  }
}

function cacheUserProfile(user: User): void {
  try {
    const map = getCacheMap();
    map[user.id] = { ...map[user.id], profile: user };
    saveCacheMap(map);
    localStorage.setItem(LAST_USER_ID_KEY, user.id);
  } catch {
    /* ignore — localStorage unavailable */
  }

  // Let main know which outlet this device is now active for, so it can
  // activate that outlet's own local PowerSync database (each outlet gets
  // its own — see main/lib/powersync/database.ts) — e.g. an admin/support
  // account signing into a different client outlet on this same machine
  // switches to that outlet's database without touching any other outlet's
  // pending data. Retries on failure (see ensureOutletSync) — a single
  // dropped attempt here must never be the reason sync never starts.
  if (user.outlet_id) {
    ensureOutletSync(user.outlet_id);
  }
}

/**
 * Returns the cached profile for `userId`, or — if omitted — the last user
 * who was cached on this device (used by the expired-token/offline fallback
 * in getCurrentUser(), where there's no session to key off of).
 */
export function getCachedUserProfile(userId?: string): User | null {
  try {
    const map = getCacheMap();
    const id = userId || localStorage.getItem(LAST_USER_ID_KEY) || '';
    return map[id]?.profile ?? null;
  } catch {
    return null;
  }
}

function getCachedUserProfileByEmail(email: string): User | null {
  try {
    const map = getCacheMap();
    const match = Object.values(map).find(
      (entry) => entry.profile.email?.toLowerCase() === email.toLowerCase(),
    );
    return match?.profile ?? null;
  } catch {
    return null;
  }
}

function cachePasscodeHash(userId: string, hash: string): void {
  const map = getCacheMap();
  map[userId] = { ...map[userId], passcodeHash: hash };
  saveCacheMap(map);
}

function clearCachedPasscodeHash(userId: string): void {
  const map = getCacheMap();
  if (map[userId]) {
    delete map[userId].passcodeHash;
    saveCacheMap(map);
  }
}

// Finds a cached user by their stored passcode hash. Since 4-digit passcodes are
// only unique PER OUTLET, an optional `allowedOutletIds` scopes the match to the
// device's outlet(s) — otherwise two staff at different outlets sharing a code
// (and therefore a hash) could resolve to the wrong person on a shared terminal.
function findCachedUserByPasscodeHash(
  hash: string,
  allowedOutletIds?: string[],
): User | null {
  const map = getCacheMap();
  const match = Object.values(map).find(
    (entry) =>
      entry.passcodeHash === hash &&
      (!allowedOutletIds ||
        (entry.profile.outlet_id != null &&
          allowedOutletIds.includes(entry.profile.outlet_id))),
  );
  return match?.profile ?? null;
}

// A missing error, one of Supabase's own dedicated "couldn't reach the
// server" error types, or a message that looks like a fetch/connectivity
// failure, is treated as "we're offline" rather than a real auth rejection.
// Shared by loginUser() and loginUserWithCode() so both sign-in flows fall
// back to a cached profile the same way instead of failing hard when the
// device has no real internet access (regardless of what navigator.onLine
// reports — see loginUserWithCode()).
//
// supabase.functions.invoke() does NOT surface a "network"/"fetch"-shaped
// message on a real connectivity failure — it wraps it in a FunctionsFetchError
// whose fixed message is "Failed to send a request to the Edge Function",
// which matches none of the regex terms below. Checking the SDK's own
// dedicated error type first is the reliable way to detect this (the message
// regex remains a fallback for anything else, e.g. the raw fetch/auth errors
// from signInWithPassword/verifyOtp, which don't have as specific a type).
function isLikelyNetworkError(error: unknown): boolean {
  if (error instanceof FunctionsFetchError) return true;
  if (!error) return true;
  const message =
    typeof error === 'string'
      ? error
      : (error as { message?: string } | null)?.message;
  return (
    !message ||
    /network|fetch|failed to fetch|load failed|offline|connection|timeout|timed out/i.test(
      message,
    )
  );
}

/**
 * Removes only `userId`'s cached profile/passcode — other staff cached on
 * this shared terminal remain able to sign in offline. Defaults to the last
 * signed-in user when no id is given.
 */
function clearCachedUserProfile(userId?: string): void {
  try {
    const map = getCacheMap();
    const id = userId || localStorage.getItem(LAST_USER_ID_KEY);
    if (id) delete map[id];
    saveCacheMap(map);
  } catch {
    /* ignore — localStorage unavailable */
  }
}

// ── ─────────────────────────────────────────────────────────────────────────

export interface SetupAccountResponse {
  success: boolean;
  user?: Partial<User>;
  companyId?: number;
  outletId?: string;
  trialEndsAt?: string;
  error?: string;
}

export const singUpUser = async ({
  email,
  password,
  firstName,
  lastName,
}: SignupPayload) => {
  try {
    const { data: authData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    });

    if (error || !authData.user) {
      throw new Error(error?.message || 'Registration failed');
    }

    return {
      success: true,
      user: {
        id: authData.user.id,
        email: authData.user.email,
        first_name: firstName,
        last_name: lastName,
      },
      requiresEmailVerification: true,
    };
  } catch (e) {
    throw new Error(e instanceof Error ? e.message : 'Registration failed');
  }
};

/**
 * Register a new user (Step 1)
 */
export async function registerUser({
  email,
  password,
  firstName,
  lastName,
}: SignupPayload): Promise<SignupResponse> {
  try {
    const { data: authData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    });

    if (error || !authData.user) {
      throw new Error(error?.message || 'Registration failed');
    }

    return {
      success: true,
      user: {
        id: authData.user.id,
        email,
        first_name: firstName,
        last_name: lastName,
      },
      requiresEmailVerification: true,
    };
  } catch (e) {
    throw new Error(e instanceof Error ? e.message : 'Registration failed');
  }
  // try {
  //   const response = await API<SignupResponse>(
  //     API_REQUEST_KEYS.AUTH_REGISTER_USER,
  //     data,
  //   );
  //   return response;
  // } catch (error) {
  //   return {
  //     success: false,
  //     error: error instanceof Error ? error.message : 'Registration failed',
  //   };
  // }
}

/**
 * Verify email with OTP (Step 2)
 */
export async function verifyEmail(
  email: string,
  token: string,
): Promise<VerifyEmailResponse> {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'signup',
    });

    if (error || !data.user) {
      // Fallback to 'email' type if 'signup' fails (sometimes required if user exists but unverified)
      const { data: retryData, error: retryError } =
        await supabase.auth.verifyOtp({
          email,
          token,
          type: 'email',
        });

      if (retryError || !retryData.user) {
        return {
          success: false,
          error: error?.message || retryError?.message || 'Verification failed',
        };
      }
      return {
        success: true,
        user: retryData.user,
      };
    }

    return {
      success: true,
      user: data.user,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Verification failed',
    };
  }
}

/**
 * Resend verification code (Step 2)
 */
export async function resendVerificationCode(
  email: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });

    if (error) {
      return {
        success: false,
        error: error.message || 'Failed to resend verification code',
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to resend verification code',
    };
  }
}

/**
 * Complete account setup - Company, Outlet, Billing (Step 3)
 */
export async function setupAccount(formData: {
  businessName: string;
  businessType: string;
  countryId: number;
  package: string;
  billingCycle?: BillingCycle;
}): Promise<SetupAccountResponse> {
  try {
    // Get current authenticated user
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return {
        success: false,
        error: 'User not authenticated',
      };
    }

    // Create company (using RPC)
    const { data: companyId, error: companyError } = await supabase.rpc(
      'create_company',
      {
        p_business_name: formData.businessName,
        p_business_type: formData.businessType,
        p_country_id: formData.countryId,
      },
    );

    if (companyError || !companyId) {
      return {
        success: false,
        error: companyError?.message || 'Failed to create company',
      };
    }

    // Create default outlet
    // User is automatically 'super' role via DB trigger, and company.created_by = user.id
    // So RLS policies will allow this operation
    const { data: outletData, error: outletError } = await supabase
      .from('outlets')
      .insert({
        company_id: companyId,
        name: formData.businessName,
        business_type: formData.businessType,
        // Let day_opened_at use its default value (CURRENT_DATE)
      })
      .select()
      .single();

    if (outletError || !outletData) {
      return {
        success: false,
        error: outletError?.message || 'Failed to create outlet',
      };
    }

    // Update user profile with outlet_id
    const { error: profileError } = await supabase
      .from('users')
      .update({ outlet_id: outletData.id })
      .eq('id', user.id);

    if (profileError) {
      return {
        success: false,
        error: profileError.message || 'Failed to update user profile',
      };
    }

    // Create billing record
    const trialEndsAt = new Date();
    trialEndsAt.setDate(trialEndsAt.getDate() + BILLING_TRIAL_PERIOD_DAYS);

    const { error: billingError } = await supabase
      .from('billing_records')
      .insert({
        billing_cycle: formData.billingCycle || 'monthly',
        company_id: companyId,
        outlet_id: outletData.id,
        package: formData.package,
        trial_ends_at: trialEndsAt.toISOString(),
        subscription_status: 'trialing',
      });

    if (billingError) {
      return {
        success: false,
        error: billingError.message || 'Failed to create billing record',
      };
    }

    return {
      success: true,
      user: {
        id: user.id,
        email: user.email || '',
        first_name: user.user_metadata.first_name,
        last_name: user.user_metadata.last_name,
        role: 'super' as UserRole,
        outlet_id: outletData.id,
        created_at: '',
        updated_at: '',
      },
      companyId,
      outletId: outletData.id,
      trialEndsAt: trialEndsAt.toISOString(),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Setup failed',
    };
  }
}

/**
 * Backwards compatibility for existing unrelated calls if any (deprecated)
 * Mapping old 'signupUser' to register -> verify -> setup is complex.
 * The onboarding flow should use the new granular methods.
 */

/**
 * Login an existing user
 */
export async function loginUser(
  email: string,
  password: string,
): Promise<LoginResponse> {
  try {
    // const response = await API<LoginResponse>(API_REQUEST_KEYS.AUTH_LOGIN, { email, password });
    // return response;
    const { data: authData, error: authError } =
      await supabase.auth.signInWithPassword({
        email,
        password,
      });

    if (authError || !authData.user) {
      // If the error looks like a network failure, check whether a valid cached
      // session already exists for this device — if so, let the user continue
      // offline using their last-known profile.
      if (isLikelyNetworkError(authError)) {
        // Attempt 1: an active (or just-refreshed) Supabase session exists.
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          const cached = getCachedUserProfile(session.user.id);
          if (cached) {
            return { success: true, user: cached };
          }
        }

        // Attempt 2: session expired or refresh failed while offline.
        // Match the cached profile by email — the network error from
        // signInWithPassword already confirms the server is unreachable
        // (not "wrong password"), so password validation is deferred.
        const cached = getCachedUserProfileByEmail(email);
        if (cached) {
          return { success: true, user: cached };
        }

        return {
          success: false,
          error:
            'No internet connection. Sign in online first to enable offline access.',
        };
      }

      return {
        success: false,
        error: authError?.message || 'Login failed',
      };
    }

    const { data: userData, error: userError } = await supabase
      .from('user_profiles_view')
      .select('*')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (userError || !userData) {
      return {
        success: false,
        error: 'Failed to fetch user profile',
      };
    }

    if (userData.is_active === false) {
      await supabase.auth.signOut({
        scope: 'local',
      });
      return {
        success: false,
        error: 'This account has been disabled. Contact your administrator.',
      };
    }

    cacheUserProfile(userData);
    clearCachedPasscodeHash(userData.id); // Clear passcode cache on standard login to prevent mismatch
    return {
      success: true,
      user: userData,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Login failed',
    };
  }
}

/**
 * Login a user with a 4-digit passcode
 */
export async function loginUserWithCode(
  code: string,
  deviceFingerprint?: string,
): Promise<LoginResponse> {
  const fingerprint = deviceFingerprint || 'unknown';

  // Helper to hash passcode in client
  const hashClientCode = async (pin: string): Promise<string> => {
    const msgUint8 = new TextEncoder().encode(pin);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  const tryOfflineFallback = async (): Promise<LoginResponse> => {
    const enteredHash = await hashClientCode(code);

    // Mirror the online flow: resolve which outlet(s) this device belongs to
    // FIRST (from the locally-synced registered_devices table), then match the
    // cached user by passcode hash WITHIN that outlet. 4-digit codes are only
    // unique per outlet, so an unscoped hash match could pick the wrong staff
    // member on a shared terminal.
    let enabledOutletIds: string[] = [];
    try {
      const dbResult = await db.execute(
        'SELECT outlet_id, is_enabled FROM registered_devices WHERE device_fingerprint = ?',
        [fingerprint],
      );
      const { rows } = dbResult;
      const rowCount = rows?.length ?? 0;
      const deviceRows: { outlet_id: string; is_enabled: number }[] = [];
      for (let i = 0; i < rowCount; i += 1) {
        deviceRows.push(
          rows!.item(i) as { outlet_id: string; is_enabled: number },
        );
      }

      if (deviceRows.length === 0) {
        return {
          success: false,
          error:
            'You are trying to sign in from an unknown device, please sign in with email and password to register this device first.',
        };
      }

      enabledOutletIds = deviceRows
        .filter((d) => d.is_enabled !== 0)
        .map((d) => d.outlet_id);

      if (enabledOutletIds.length === 0) {
        return {
          success: false,
          error:
            'This device is disabled. Please contact your administrator to enable it.',
        };
      }
    } catch (err) {
      // If the local device table can't be read, fall back to a best-effort
      // unscoped match rather than blocking a legitimate offline sign-in.
      console.error(
        '[OfflineAuth] Failed to query local registered devices:',
        err,
      );
    }

    const cachedProfile = findCachedUserByPasscodeHash(
      enteredHash,
      enabledOutletIds.length > 0 ? enabledOutletIds : undefined,
    );

    if (!cachedProfile) {
      return {
        success: false,
        error:
          'No internet connection. Please verify your passcode online first to enable offline access.',
      };
    }

    return { success: true, user: cachedProfile };
  };

  try {
    // Always attempt the online path first, regardless of navigator.onLine —
    // that API only reflects the local network interface's link state, not
    // real internet reachability (e.g. Wi-Fi connected with no upstream
    // route, captive portal, DNS/ISP outage), so pre-checking it can skip
    // straight to a failing "online" attempt even when the device is
    // genuinely offline. Falls back to the cached profile below whenever the
    // failure looks network-related, matching loginUser()'s shape.
    const { data, error: functionError } = await supabase.functions.invoke(
      'login-with-code',
      {
        body: {
          login_code: code,
          device_fingerprint: fingerprint,
        },
      },
    );

    if (functionError) {
      if (isLikelyNetworkError(functionError)) {
        return await tryOfflineFallback();
      }
      return {
        success: false,
        error: functionError.message || 'Login failed',
      };
    }

    if (data?.error) {
      return {
        success: false,
        error: data.error,
      };
    }

    const { email, email_otp } = data;

    // Verify OTP using Native Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.verifyOtp({
      email,
      token: email_otp,
      type: 'magiclink',
    });

    if (authError || !authData.user) {
      if (isLikelyNetworkError(authError)) {
        return await tryOfflineFallback();
      }
      return {
        success: false,
        error: authError?.message || 'Verification failed',
      };
    }

    // Fetch full user profile
    const { data: userData, error: userError } = await supabase
      .from('user_profiles_view')
      .select('*')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (userError || !userData) {
      return {
        success: false,
        error: 'Failed to fetch user profile',
      };
    }

    if (userData.is_active === false) {
      await supabase.auth.signOut({ scope: 'local' });
      return {
        success: false,
        error: 'This account has been disabled. Contact your administrator.',
      };
    }

    cacheUserProfile(userData);

    // Save this user's passcode hash locally for offline verification
    const clientHash = await hashClientCode(code);
    cachePasscodeHash(userData.id, clientHash);

    return {
      success: true,
      user: userData,
    };
  } catch (error) {
    // A thrown network error (e.g. functions.invoke rejecting outright
    // rather than resolving with an `error` field) should also fall back.
    if (isLikelyNetworkError(error)) {
      return tryOfflineFallback();
    }
    const message = error instanceof Error ? error.message : String(error);
    return {
      success: false,
      error: message || 'Login failed',
    };
  }
}

/**
 * Register the current device fingerprint for the specified outlet
 */
export async function registerCurrentDevice(
  outletId: string,
): Promise<ApiResponse<void>> {
  try {
    const deviceDetails = (await window.electron.ipcRenderer.invoke(
      'system:getDeviceDetails',
    )) as any;
    if (!deviceDetails?.success || !deviceDetails.data) {
      return {
        success: false,
        error: deviceDetails?.error || 'Failed to get device details',
      };
    }

    const { device_fingerprint, device_name, operating_system } =
      deviceDetails.data;

    const { error } = await supabase.from('registered_devices').upsert(
      {
        outlet_id: outletId,
        device_fingerprint,
        device_name,
        operating_system,
      },
      { onConflict: 'outlet_id, device_fingerprint' },
    );

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to register device',
    };
  }
}

/**
 * Logout the current user
 *
 * Always succeeds, online or offline. Local PowerSync data is preserved
 * across logout (see lib/powersync/database.ts) so pending unsynced changes
 * stay safely queued and upload once connectivity returns — sign-out must
 * never block a shift hand-off between staff sharing the same terminal.
 *
 * The cached profile/passcode is deliberately NOT cleared here — a user who
 * has ever signed in on this machine must still be able to sign in offline
 * afterwards, including right after their own sign-out (e.g. a waiter
 * signing out and back in offline mid-shift). The only path that revokes
 * offline access is a confirmed `is_active === false` from a fresh online
 * fetch (see getCurrentUser()/loginUser()/loginUserWithCode() below), which
 * inherently requires network access to detect.
 */
export async function logoutUser(): Promise<void> {
  await supabase.auth.signOut({
    scope: 'local',
  });
}

/**
 * Start Google OAuth sign-in flow.
 * - Desktop: gets the OAuth URL from Supabase (PKCE, skipBrowserRedirect),
 *   then sends it to the main process to open in the system browser.
 *   The main process will send back the callback URL via 'auth:googleCallback'.
 * - Web: triggers a full-page redirect to Google; Supabase handles the
 *   return via detectSessionInUrl.
 */
export async function signInWithGoogle(): Promise<void> {
  if (isElectron()) {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        skipBrowserRedirect: true,
        redirectTo: 'smartpos-cloud://auth/callback',
      },
    });
    if (error || !data.url) {
      throw new Error(error?.message || 'Failed to start Google sign-in');
    }
    await (window as any).electron.ipcRenderer.invoke(
      'auth:googleSignIn',
      data.url,
    );
  } else {
    // Use the current origin as redirectTo so Supabase sends the user back to
    // the correct URL (production or local dev) rather than the Supabase
    // Site URL default (which may still point to localhost:3000).
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) throw new Error(error.message);
  }
}

/**
 * Returns the raw Supabase auth user regardless of whether an app profile
 * exists. Used after OAuth to detect new users who have not yet set up a
 * company/outlet.
 */
export async function getAuthSessionUser() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
}

/**
 * Get the current user session
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    // getSession() reads the stored session from localStorage — no network
    // needed. getUser() validates the JWT server-side and always requires
    // network; using getSession() here allows offline auto-restore.
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const authUserId = session?.user?.id ?? null;

    if (!authUserId) {
      // No active session (never logged in, explicitly signed out, or the
      // token expired while offline). Returning the cached profile here is
      // intentional even after an explicit logout — offline sign-in must
      // keep working for a user who has ever used this machine, including
      // right after their own sign-out. This is only ever reached from an
      // active login attempt (fetchCurrentUser() is never dispatched on its
      // own after logout), so it can't silently "re-log in" someone who
      // hasn't tried to sign in again.
      return getCachedUserProfile();
    }

    const { data: userData, error } = await supabase
      .from('user_profiles_view')
      .select('*')
      .eq('id', authUserId)
      .maybeSingle();

    if (error || !userData) {
      // Network failed — return the locally-cached profile for this session's user.
      const cached = getCachedUserProfile(authUserId);
      if (cached) {
        return cached;
      }
      return null;
    }

    if (userData.is_active === false) {
      await supabase.auth.signOut({ scope: 'local' });
      clearCachedUserProfile(authUserId);
      return null;
    }

    cacheUserProfile(userData); // keep cache fresh on each successful fetch
    return userData;
  } catch {
    return null;
  }
}
